import { useCallback, useEffect, useMemo, useState } from "react";
import { Bookmark, Check, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import Dialog from "@/components/atoms/Dialog";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { scheduleDeferredDeleteWithUndo } from "@/lib/deferredDeleteWithUndo";
import {
  savedFiltersService,
  type SavedFilter,
  type SavedFilterConfig,
} from "@/services/savedFiltersService";
import {
  usePageScope,
  useSettingsStore,
  type ScopePageKey,
} from "@/store/settingsStore";

interface Props {
  pageName: string;
  filterParamKeys?: readonly string[];
  scopePage?: ScopePageKey;
  captureFilterState?: () => Record<string, unknown>;
  applyFilterState?: (state: Record<string, unknown>) => void;
}

const NO_FILTER_PARAM_KEYS: readonly string[] = [];

function selectParams(current: URLSearchParams, keys: ReadonlySet<string>): string {
  const selected = new URLSearchParams();
  current.forEach((value, key) => {
    if (keys.has(key)) selected.append(key, value);
  });
  selected.sort();
  return selected.toString();
}

/** Shared user-level save/apply control for URL and component-state list filters. */
export function SavedFiltersButton({
  pageName,
  filterParamKeys = NO_FILTER_PARAM_KEYS,
  scopePage,
  captureFilterState,
  applyFilterState,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedKeys = useMemo(() => new Set(filterParamKeys), [filterParamKeys]);
  const scope = usePageScope(scopePage ?? "contacts");
  const setScope = useSettingsStore((state) => state.setScope);
  const scopeLabels = useSettingsStore((state) => state.scopeLabels);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [items, setItems] = useState<SavedFilter[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await savedFiltersService.list(pageName));
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "Saved filters could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [pageName]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const currentConfig = useCallback((): SavedFilterConfig => ({
    query: selectParams(searchParams, allowedKeys),
    ...(captureFilterState ? { state: captureFilterState() } : {}),
    ...(scopePage
      ? {
          scope: {
            legalEntityIds: Array.isArray(scope?.legalEntityIds) ? [...scope.legalEntityIds] : [],
            branchIds: Array.isArray(scope?.branchIds) ? [...scope.branchIds] : [],
          },
        }
      : {}),
  }), [allowedKeys, captureFilterState, scope, scopePage, searchParams]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const created = await savedFiltersService.create({
        name: trimmed,
        pageName,
        filterConfig: currentConfig(),
      });
      setItems((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name)));
      setName("");
      toast.success(`Saved “${created.name}”.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "The filter could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // `save` handles expected API failures itself; keep a terminal catch here so
    // event-handler invocation can never leave a rejected promise unobserved.
    void save().catch((error) => {
      toast.error(getApiErrorMessage(error) ?? "The filter could not be saved.");
    });
  };

  const apply = (saved: SavedFilter) => {
    const next = new URLSearchParams(searchParams);
    for (const key of allowedKeys) next.delete(key);
    const savedParams = new URLSearchParams(saved.filterConfig?.query ?? "");
    savedParams.forEach((value, key) => {
      // Treat server data as untrusted and restore only filters owned by this page.
      if (allowedKeys.has(key)) next.append(key, value);
    });
    if (scopePage && saved.filterConfig?.scope) {
      const savedScope = saved.filterConfig.scope;
      const legalEntityIds = Array.isArray(savedScope.legalEntityIds) ? savedScope.legalEntityIds : [];
      const branchIds = Array.isArray(savedScope.branchIds) ? savedScope.branchIds : [];
      const selectedLabels: Record<string, string> = {};
      for (const id of [...legalEntityIds, ...branchIds]) {
        if (scopeLabels[id]) selectedLabels[id] = scopeLabels[id];
      }
      setScope(scopePage, {
        legalEntityIds,
        branchIds,
      }, selectedLabels);
    }
    applyFilterState?.(saved.filterConfig?.state ?? {});
    if (allowedKeys.size > 0) setSearchParams(next, { replace: true });
    setOpen(false);
    toast.success(`Applied “${saved.name}”.`);
  };

  const remove = (saved: SavedFilter) => {
    const restore = () => {
      setItems((current) => {
        if (current.some((item) => item.id === saved.id)) return current;
        return [...current, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
    };

    scheduleDeferredDeleteWithUndo({
      idKey: `saved-filter:${saved.id}`,
      label: saved.name,
      hint: "This saved filter will be permanently deleted.",
      onScheduled: () => {
        setItems((current) => current.filter((item) => item.id !== saved.id));
      },
      onUndo: restore,
      onFailed: restore,
      onCommit: () => savedFiltersService.delete(saved.id),
      onComplete: () => {
        // A reload during the undo window may have returned the pending item.
        setItems((current) => current.filter((item) => item.id !== saved.id));
        toast.success(`Deleted “${saved.name}”.`);
      },
    });
  };

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Bookmark size={14} /> Saved filters
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Saved filters"
        subtitle="Save the filters currently applied to this page, or restore a previous view."
        submitLabel={saving ? "Saving…" : "Save current filters"}
        submitClassName="bg-bridge-accent hover:bg-bridge-accentSoft"
        submitDisabled={!name.trim() || saving}
        onSubmit={handleSave}
      >
        <div className="space-y-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-bridge-fg" htmlFor={`${pageName}-filter-name`}>
              Filter name
            </label>
            <Input
              id={`${pageName}-filter-name`}
              value={name}
              maxLength={100}
              placeholder="e.g. Failed syncs"
              leading={<Save size={15} />}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSave();
                }
              }}
            />
            {!name.trim() && <p className="mt-1.5 text-xs text-bridge-muted">Enter a name to save this view.</p>}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-bridge-fg">Your filters</h3>
            {loading ? (
              <div className="flex items-center gap-2 py-5 text-sm text-bridge-muted">
                <Loader2 size={16} className="animate-spin" /> Loading saved filters…
              </div>
            ) : items.length === 0 ? (
              <p className="rounded-md border border-dashed border-bridge-border p-4 text-sm text-bridge-muted">
                You have no saved filters for this page yet.
              </p>
            ) : (
              <ul className="divide-y divide-bridge-border/60 rounded-md border border-bridge-border/70">
                {items.map((saved) => (
                  <li key={saved.id} className="flex items-center gap-3 px-3 py-2.5">
                    <Bookmark size={15} className="shrink-0 text-bridge-accent" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-bridge-fg">{saved.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => apply(saved)}>
                      <Check size={14} /> Apply
                    </Button>
                    <button
                      type="button"
                      aria-label={`Delete ${saved.name}`}
                      onClick={() => remove(saved)}
                      className="rounded-md p-2 text-bridge-muted hover:bg-red-50 hover:text-bridge-danger"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
